/**
 * Regulamin Brykolandii — the house rules behind the § list on
 * /brykolandia/regulamin.
 *
 * Verbatim from the client's "regulamin gotowy" PDF (September 2026); the
 * section titles and summaries come from the page copy in the content PDF.
 * Edit the text here — there is no CMS behind it. `**…**` marks the PDF's
 * bold spans and renders as <strong>.
 */

export interface RuleClause {
  /** Clause number, rendered as the bold "1." lead. */
  n: number
  /** Bold point title. Only §5 has them ("Rezerwacja i zaliczka"). */
  title?: string
  /** Paragraphs before any list. */
  text?: readonly string[]
  /** a) b) c) list; an entry can carry its own bullets (§1.1 a). */
  letters?: readonly { text: string; bullets?: readonly string[] }[]
  bullets?: readonly string[]
  /** Paragraphs after the list. */
  after?: readonly string[]
}

export interface RuleGroup {
  /** "A. Rezerwacja urodzin" — §5 is split into lettered parts. */
  heading: string
  clauses: readonly RuleClause[]
}

export interface RuleSection {
  /** URL fragment: /brykolandia/regulamin#organizacja-urodzin opens §5. */
  id: string
  /** "01", shown as the red "§ 01". */
  number: string
  title: string
  /** One line under the title, visible while the section is collapsed. */
  summary: string
  /** Small grey tag after the summary. */
  tag?: string
  clauses?: readonly RuleClause[]
  groups?: readonly RuleGroup[]
}

/** "Pamiętaj o najważniejszych zasadach" — the band above the § list. */
export const BRYKOLANDIA_KEY_RULES = [
  'czyste skarpety lub rajstopy',
  'bez jedzenia i napojów na konstrukcji',
  'opróżnione kieszenie',
  'dzieci do 5 lat pod stałą opieką osoby dorosłej'
] as const

/** The birthday rules (§5) — "Regulamin urodzin" buttons link straight to it. */
export const BIRTHDAY_RULES_ID = 'organizacja-urodzin'

export const BRYKOLANDIA_RULES: readonly RuleSection[] = [
  {
    id: 'postanowienia-ogolne',
    number: '01',
    title: 'Postanowienia ogólne',
    summary:
      'Informacje o Brykolandii, godzinach otwarcia, wieku dzieci oraz zasadach opieki nad najmłodszymi.',
    clauses: [
      {
        n: 1,
        text: [
          'Bawialnia Brykolandia, znajdująca się w budynku PUHW Perfekt przy ul. Żeromskiego 64A w Skarżysku-Kamiennej, oferuje:'
        ],
        letters: [
          {
            text: 'salę zabaw obejmującą:',
            bullets: [
              'salę urodzinową z domkami i zabawkami twardymi,',
              'salę z konstrukcją zabawową i zabawkami miękkimi,',
              'korytarz wyposażony w zabawki stacjonarne;'
            ]
          },
          {
            text: 'organizację przyjęć urodzinowych oraz innych imprez okolicznościowych dla dzieci, zgodnie z aktualną ofertą i cennikiem.'
          }
        ]
      },
      {
        n: 2,
        text: [
          'Brykolandia jest czynna od poniedziałku do soboty w aktualnie obowiązujących godzinach otwarcia i przeznaczona jest dla dzieci w wieku od 2 do 12 lat.'
        ]
      },
      {
        n: 3,
        text: [
          'Dzieci do ukończenia 5. roku życia mogą przebywać w Brykolandii wyłącznie pod stałą opieką osoby dorosłej.'
        ]
      }
    ]
  },
  {
    id: 'bezpieczenstwo-i-higiena',
    number: '02',
    title: 'Bezpieczeństwo i higiena zabawy',
    summary:
      'Zasady bezpiecznego korzystania z sali zabaw, obowiązujące zakazy oraz wymagania higieniczne.',
    clauses: [
      {
        n: 1,
        text: [
          'Na teren Brykolandii nie wolno wnosić przedmiotów mogących stanowić zagrożenie dla bezpieczeństwa podczas zabawy. Przed jej rozpoczęciem należy opróżnić kieszenie z takich przedmiotów oraz wyjąć gumę do żucia.'
        ]
      },
      {
        n: 2,
        text: [
          'Przed rozpoczęciem zabawy należy odpowiednio zabezpieczyć przedmioty mogące ulec zniszczeniu, w szczególności telefony komórkowe, aparaty fotograficzne i inne urządzenia elektroniczne.'
        ]
      },
      {
        n: 3,
        text: ['Na terenie Brykolandii zabrania się:'],
        letters: [
          { text: 'zeskakiwania z elementów konstrukcji zabawowej,' },
          { text: 'wchodzenia pod górę zjeżdżalni,' },
          {
            text: 'wspinania się po wewnętrznych lub zewnętrznych ściankach nieprzeznaczonych do tego celu,'
          },
          {
            text: 'niszczenia elementów konstrukcji, wystroju wnętrz, zabawek oraz pozostałego wyposażenia,'
          },
          { text: 'popychania innych uczestników zabawy,' },
          { text: 'biegania po korytarzu i sali urodzinowej,' },
          {
            text: 'wynoszenia piłeczek z basenu oraz wnoszenia twardych zabawek do sali z konstrukcją zabawową,'
          },
          {
            text: 'spożywania słodyczy, innych pokarmów oraz napojów podczas zabawy na konstrukcji ze względu na ryzyko zadławienia,'
          },
          {
            text: 'zachowywania się w sposób mogący spowodować upadek, uraz lub inne zagrożenie dla dziecka, rodzica, opiekuna lub innej osoby,'
          },
          {
            text: 'korzystania z urządzeń i wyposażenia Brykolandii niezgodnie z ich przeznaczeniem.'
          }
        ]
      },
      {
        n: 4,
        text: [
          'Podczas korzystania ze zjeżdżalni należy zachować bezpieczny odstęp pomiędzy zjeżdżającymi osobami.'
        ]
      },
      {
        n: 5,
        text: [
          'W trosce o zdrowie wszystkich uczestników zabawy w Brykolandii nie powinny przebywać osoby z chorobami zakaźnymi, wirusowymi lub bakteryjnymi mogącymi stanowić zagrożenie dla innych osób.'
        ]
      },
      {
        n: 6,
        text: [
          'Rodzic lub opiekun zobowiązany jest poinformować personel Brykolandii o schorzeniach, ograniczeniach lub niepełnej sprawności dziecka, jeżeli mogą one mieć znaczenie dla jego bezpiecznego korzystania z sali zabaw.'
        ]
      },
      {
        n: 7,
        text: [
          'Na teren Brykolandii należy wchodzić w czystych skarpetach lub rajstopach. Niedozwolone jest przebywanie w sali zabaw w obuwiu lub boso.'
        ]
      },
      {
        n: 8,
        text: [
          'W szatni dostępne są kapcie i japonki dla osób dorosłych, przeznaczone wyłącznie do użytku na terenie Brykolandii. Osoby opuszczające Brykolandię, nawet na krótki czas, zobowiązane są przed wyjściem założyć własne obuwie.'
        ]
      },
      {
        n: 9,
        text: [
          'Na konstrukcję zabawową można wchodzić wyłącznie w skarpetach lub rajstopach. Ze względu na zastosowanie materiałów antypoślizgowych korzystanie z konstrukcji w jakimkolwiek obuwiu jest niedozwolone i może grozić urazem, w szczególności skręceniem kostki.'
        ]
      },
      {
        n: 10,
        text: [
          'Ze względów higienicznych wszystkie napoje i pokarmy należy spożywać w sali urodzinowej. Nie wolno wnosić ich do sali z konstrukcją zabawową.'
        ]
      },
      {
        n: 11,
        text: [
          'Do toalet należy wchodzić wyłącznie w specjalnych „kapciach-szczurkach”, znajdujących się w korytarzu przed toaletami. „Kapcie-szczurki” mogą być używane wyłącznie w obrębie toalet.'
        ]
      },
      {
        n: 12,
        text: [
          'Osoby zachowujące się w sposób dezorganizujący funkcjonowanie Brykolandii, stwarzające zagrożenie dla siebie lub innych albo niestosujące się do zaleceń personelu mogą nie zostać wpuszczone do Brykolandii lub zostać poproszone o jej opuszczenie.'
        ]
      },
      {
        n: 13,
        text: [
          'Wszystkie osoby przebywające na terenie Brykolandii zobowiązane są do przestrzegania poleceń personelu dotyczących bezpieczeństwa i zasad korzystania z obiektu.'
        ]
      },
      {
        n: 14,
        text: [
          'Personel Brykolandii nie sprawuje indywidualnej opieki nad dziećmi, z wyjątkiem usług, których charakter obejmuje zapewnienie przez organizatora opieki nad uczestnikami.'
        ]
      }
    ]
  },
  {
    id: 'zasady-porzadkowe',
    number: '03',
    title: 'Ogólne zasady i sprawy porządkowe',
    summary: 'Zasady dotyczące szatni, rzeczy osobistych, zabaw i obowiązki klientów.',
    clauses: [
      {
        n: 1,
        text: [
          'Odzież wierzchnią oraz obuwie należy pozostawić w szatni. Osoby korzystające z Brykolandii przebywają na jej terenie w skarpetach, rajstopach lub przeznaczonych do tego kapciach.'
        ]
      },
      {
        n: 2,
        text: ['Wszelkie przedmioty wartościowe należy zabrać ze sobą. Szatnia nie jest strzeżona.']
      },
      {
        n: 3,
        text: [
          'Personel i właściciel Brykolandii nie ponoszą odpowiedzialności za rzeczy pozostawione lub zgubione na terenie Brykolandii, z zastrzeżeniem odpowiedzialności wynikającej z bezwzględnie obowiązujących przepisów prawa.'
        ]
      },
      {
        n: 4,
        text: [
          'Na terenie całego budynku PUHW Perfekt, w tym Brykolandii, obowiązuje bezwzględny zakaz palenia papierosów, spożywania alkoholu oraz wprowadzania zwierząt.'
        ]
      },
      {
        n: 5,
        text: [
          'Personel Brykolandii jest odpowiednio przeszkolony i dokłada wszelkich starań w celu zapewnienia bezpieczeństwa oraz odpowiednich warunków higienicznych podczas zabawy dzieci.'
        ]
      },
      {
        n: 6,
        text: [
          'Rodzic lub opiekun zobowiązany jest zapewnić dziecku odpowiednie skarpety lub rajstopy wymagane do bezpiecznego korzystania z Brykolandii zgodnie z niniejszym regulaminem.'
        ]
      }
    ]
  },
  {
    id: 'odpowiedzialnosc',
    number: '04',
    title: 'Odpowiedzialność',
    summary: 'Informacje o odpowiedzialności za dzieci oraz za szkody i rzeczy osobiste.',
    clauses: [
      {
        n: 1,
        text: ['Właściciel Brykolandii posiada ubezpieczenie odpowiedzialności cywilnej.']
      },
      {
        n: 2,
        text: [
          'Za szkody na osobach lub mieniu spowodowane przez dziecko odpowiada jego rodzic lub opiekun prawny na zasadach określonych w obowiązujących przepisach prawa.'
        ]
      },
      {
        n: 3,
        text: [
          'Korzystanie z sali zabaw wiąże się z aktywnością ruchową i może skutkować zabrudzeniem lub uszkodzeniem odzieży. Właściciel ani personel Brykolandii nie ponoszą odpowiedzialności za takie szkody, o ile nie wynikają one z okoliczności, za które zgodnie z obowiązującymi przepisami odpowiedzialność ponosi właściciel Brykolandii.'
        ]
      },
      {
        n: 4,
        text: [
          'Brykolandia nie ponosi odpowiedzialności za skutki korzystania z urządzeń i wyposażenia w sposób niezgodny z ich przeznaczeniem lub niniejszym regulaminem, o ile szkoda nie wynika również z okoliczności, za które odpowiedzialność zgodnie z obowiązującymi przepisami ponosi właściciel Brykolandii.'
        ]
      },
      {
        n: 5,
        text: [
          'Rodzice, opiekunowie oraz pozostali uczestnicy zobowiązani są do przestrzegania zasad bezpieczeństwa określonych w regulaminie oraz poleceń personelu.'
        ]
      }
    ]
  },
  {
    id: BIRTHDAY_RULES_ID,
    number: '05',
    title: 'Organizacja przyjęć urodzinowych',
    summary:
      'Zasady rezerwacji, płatności, czasu trwania, liczby gości, jedzenia, przedłużenia i odwołania przyjęcia.',
    tag: 'dotyczy urodzin',
    groups: [
      {
        heading: 'A. Rezerwacja urodzin',
        clauses: [
          {
            n: 1,
            title: 'Rezerwacja i zaliczka',
            text: [
              'Urodziny można zarezerwować telefonicznie lub osobiście. W ciągu 3 dni od rezerwacji należy wpłacić zaliczkę **100 zł**. Jeśli rezerwacja jest dokonywana **14 dni lub mniej przed imprezą**, zaliczkę należy wpłacić w dniu zapisu. Brak wpłaty w terminie powoduje anulowanie rezerwacji.'
            ]
          },
          {
            n: 2,
            title: 'Rezygnacja lub zmiana terminu',
            text: ['W przypadku odwołania lub zmiany rezerwacji:'],
            bullets: [
              '**powyżej 21 dni przed urodzinami** – zwracamy całą zaliczkę,',
              '**21 dni lub mniej przed urodzinami** – zaliczka 100 zł nie jest zwracana, a zamawiający otrzymuje **karnet na 5 godzin zabawy**, ważny przez 1 rok.'
            ]
          },
          {
            n: 3,
            title: 'Liczba imprez',
            text: [
              'W tym samym czasie w Brykolandii mogą odbywać się maksymalnie **dwie imprezy urodzinowe**. Można dodatkowo zarezerwować drugie stoliki w sali urodzinowej – wtedy w tym czasie odbywa się tylko jedna impreza.'
            ]
          }
        ]
      },
      {
        heading: 'B. Urodziny w wariantach MINI, STANDARD i MAXI',
        clauses: [
          {
            n: 1,
            title: 'Zaproszenia',
            text: ['Zamawiający otrzymuje w Brykolandii zaproszenia dla gości.']
          },
          {
            n: 2,
            title: 'Czas zabawy',
            text: [
              'Urodziny trwają **2 godziny w pakietach MINI i STANDARD, a 3 godziny w pakiecie MAXI**.'
            ]
          },
          {
            n: 3,
            title: 'Poczęstunek',
            text: [
              'Dla dzieci przygotowujemy stolik z kolorową zastawą i słodkim poczęstunkiem. Produkty są uzupełniane przez cały czas urodzin i dzieci mogą korzystać z nich bez ograniczeń. To, co pozostanie po imprezie, może zostać zabrane przez zamawiającego lub gości.'
            ]
          },
          {
            n: 4,
            title: 'Przebranie jubilata i wręczanie prezentów',
            text: [
              'Na czas wręczania prezentów i poczęstunku tortem jubilat może założyć jeden ze strojów dostępnych w Brykolandii. W stroju nie wolno biegać po konstrukcji zabawowej.',
              'Jubilat wybiera również strój dla animatora, który towarzyszy mu podczas tej części imprezy. Dla jubilata przygotowane jest specjalne miejsce, w którym odbywa się uroczyste wręczanie prezentów.'
            ]
          },
          {
            n: 5,
            title: 'Tort',
            text: [
              'Brykolandia nie zapewnia tortu. Tort organizuje zamawiający, a my możemy przechować go w dniu imprezy.'
            ]
          },
          {
            n: 6,
            title: 'Malowanie twarzy',
            text: [
              'Gościom urodzinowym malujemy twarze wysokiej jakości profesjonalnymi farbami przeznaczonymi do tego celu i niewywołującymi reakcji alergicznych.'
            ]
          },
          {
            n: 7,
            title: 'Peruki, czapki i inne ozdoby',
            text: [
              'Dzieci mogą korzystać z peruk, czapek i innych ozdób do przebierania się i robienia zdjęć. Nie wolno w nich biegać po konstrukcji zabawowej.'
            ]
          },
          {
            n: 8,
            title: 'Prezent dla jubilata',
            text: ['Jubilat otrzymuje **karnet na zabawę w Brykolandii** (ważny 1 rok):'],
            bullets: [
              '**1 godzinę w pakiecie MINI**',
              '**3 godziny w pakiecie STANDARD**',
              '**5 godzin w pakiecie MAXI**'
            ],
            after: [
              'Przy dwóch jubilatach dwa karnety przysługują wtedy, gdy w urodzinach uczestniczy łącznie minimum **14 osób**.'
            ]
          },
          {
            n: 9,
            title: 'Przebieg imprezy',
            text: [
              'Imprezę prowadzi i nadzoruje personel Brykolandii. Zamawiający jest informowany o planowanym przebiegu urodzin. Kolejność atrakcji może się zmieniać w zależności od liczby i wieku dzieci oraz charakteru imprezy.'
            ]
          },
          {
            n: 10,
            title: 'Uwagi i sugestie',
            text: [
              'Personel dokłada wszelkich starań, aby impreza była udana, i jest otwarty na uwagi oraz sugestie dzieci i opiekunów.'
            ]
          }
        ]
      },
      {
        heading: 'C. Urodziny tematyczne',
        clauses: [
          {
            n: 1,
            title: 'Zakres pakietu',
            text: ['Urodziny tematyczne obejmują wszystkie usługi dostępne w pakiecie MAXI.']
          },
          {
            n: 2,
            title: 'Animacje',
            text: [
              'Przez **45 minut** dzieci uczestniczą w prowadzonych przez animatora zabawach i konkurencjach związanych z wybraną tematyką urodzin.'
            ]
          },
          {
            n: 3,
            title: 'Udział w animacjach',
            text: [
              'Jeśli dziecko nie chce uczestniczyć w animacjach, np. płacze, ucieka lub utrudnia ich prowadzenie, może bawić się samodzielnie w Brykolandii.'
            ]
          }
        ]
      },
      {
        heading: 'D. Dodatkowe atrakcje',
        clauses: [
          {
            n: 1,
            title: 'Przedłużenie urodzin',
            text: [
              'Urodziny można przedłużyć w godzinach otwarcia Brykolandii – z poczęstunkiem lub bez niego. Przedłużenie z poczęstunkiem jest możliwe tylko wtedy, gdy stoliki nie są zarezerwowane na kolejną imprezę.'
            ]
          },
          {
            n: 2,
            title: 'Trampolina',
            text: [
              'Trampolina jest dostępna w sezonie **wiosna–jesień**, przy sprzyjającej pogodzie.'
            ]
          },
          {
            n: 3,
            title: 'Dyskoteka i animacje',
            text: [
              'Dyskoteka z laserami i dymem („mgłą”) oraz animacje odbywają się na terenie Brykolandii.'
            ]
          },
          {
            n: 4,
            title: 'Stoliki dla dorosłych',
            text: ['Dodatkowe stoliki dla dorosłych obejmują:'],
            bullets: [
              'rezerwację stolików w sali urodzinowej na czas imprezy,',
              'zastawę,',
              'zestaw produktów spożywczych zależny od wybranego wariantu 1, 2 lub 3.'
            ],
            after: ['W przypadku przedłużenia urodzin obowiązuje odpowiednia dopłata.']
          },
          {
            n: 5,
            title: 'Płyta ze zdjęciami',
            text: [
              'Płyta z urodzinowym nadrukiem zawiera **40–50 wybranych i komputerowo obrobionych zdjęć** z imprezy. O możliwości odbioru gotowej płyty informujemy telefonicznie.'
            ]
          },
          {
            n: 6,
            title: 'Termin zamawiania dodatkowych atrakcji',
            text: [
              'Dodatkowe usługi i atrakcje, np. zdjęcia, animacje czy dyskotekę, należy zgłosić najpóźniej **1 dzień przed urodzinami**.'
            ]
          }
        ]
      },
      {
        heading: 'E. Rozliczenie urodzin',
        clauses: [
          {
            n: 1,
            title: 'Liczba płatnych gości',
            text: [
              'Opłata jest naliczana za rzeczywistą liczbę dzieci uczestniczących w urodzinach, a nie za liczbę wcześniej zadeklarowaną. Minimalna opłata odpowiada jednak **7 pełnopłatnym gościom**.',
              'Nie pobieramy opłaty za osoby dorosłe oraz dzieci raczkujące lub pozostające na rękach rodziców.'
            ]
          },
          {
            n: 2,
            title: 'Dzieci do 3 lat',
            text: [
              'Za dzieci do 3 lat, które nie korzystają z atrakcji urodzinowych ani poczęstunku, pobieramy wyłącznie opłatę za zabawę w Brykolandii zgodnie z aktualnym cennikiem.'
            ]
          },
          {
            n: 3,
            title: 'Jubilat gratis',
            text: [
              'Jeśli w urodzinach uczestniczy **więcej niż 7 pełnopłatnych dzieci**, jubilat nie płaci za udział.'
            ]
          },
          {
            n: 4,
            title: 'Kogo obejmuje opłata',
            text: [
              'Opłata jest naliczana za wszystkie dzieci, które przyszły na urodziny i korzystają z atrakcji urodzinowych oraz poczęstunku.'
            ]
          },
          {
            n: 5,
            title: '„Nieoficjalni goście”',
            text: [
              'Jeśli gościom urodzinowym towarzyszą inne dzieci, które nie są oficjalnymi gośćmi imprezy, pobieramy za nie opłatę jak za gościa urodzinowego, jeśli dzieci korzystają z atrakcji urodzinowych i poczęstunku.',
              'Jeżeli dziecko nie korzysta z atrakcji urodzinowych i posiłku, naliczamy jedynie opłatę za zabawę w Brykolandii zgodnie z aktualnym cennikiem.'
            ]
          },
          {
            n: 6,
            title: 'Kto płaci za „nieoficjalnego gościa”',
            text: [
              'Zamawiający powinien wcześniej ustalić z rodzicami takiego dziecka, kto pokrywa jego opłatę – rodzic czy zamawiający.'
            ]
          },
          {
            n: 7,
            title: 'Dwóch jubilatów',
            text: [
              'Przy dwóch jubilatach Brykolandia dokonuje jednego rozliczenia za całą imprezę. Ewentualne zniżki przysługujące jubilatom są naliczane od połowy kosztu wspólnej imprezy. Zamawiający sami ustalają między sobą sposób podziału kosztów.'
            ]
          },
          {
            n: 8,
            title: 'Karnety',
            text: [
              'Karnetów do Brykolandii nie można wykorzystać do opłacenia urodzin ani pobytu „nieoficjalnych gości”.'
            ]
          },
          {
            n: 9,
            title: 'Zniżka na kolejne urodziny',
            text: ['Przy organizacji:'],
            bullets: [
              'drugich urodzin tego samego jubilata – **5% zniżki**,',
              'trzecich urodzin – **10% zniżki**.'
            ],
            after: ['Zniżka nie obejmuje produktów kupowanych w sklepiku Brykolandii.']
          },
          {
            n: 10,
            title: 'Przedłużenie urodzin',
            text: [
              'Opłata za przedłużenie urodzin jest naliczana za każde rozpoczęte dodatkowe **15 minut** pobytu dzieci.'
            ]
          },
          {
            n: 11,
            title: 'Godzina przyjścia',
            text: [
              'Na urodziny zapraszamy najwcześniej **10 minut przed rozpoczęciem imprezy**. W przypadku wcześniejszego przybycia naliczamy dodatkowe opłaty zgodnie z cennikiem Brykolandii.'
            ]
          },
          {
            n: 12,
            title: 'Zniszczenie napisu urodzinowego',
            text: [
              'Za każdą zniszczoną literkę z napisu urodzinowego pobierana jest opłata **15 zł**.'
            ]
          },
          {
            n: 13,
            title: 'Łączenie promocji',
            text: [
              'Zniżki i promocje nie łączą się. Zawsze proponujemy zamawiającemu wariant najkorzystniejszy finansowo.'
            ]
          }
        ]
      },
      {
        heading: 'F. Bezpieczeństwo i higiena',
        clauses: [
          {
            n: 1,
            title: 'Obuwie i skarpety',
            text: [
              'Na teren Brykolandii nie wolno wchodzić w obuwiu ani boso. W szatni dostępne są „kapcie” i „japonki” dla dorosłych, przeznaczone wyłącznie do używania w Brykolandii.',
              'Osoby, które na chwilę wychodzą z Brykolandii, muszą poza jej obrębem założyć obuwie.',
              'Zamawiający ma obowiązek poinformować swoich gości o konieczności zabrania **skarpet**.'
            ]
          },
          {
            n: 2,
            title: 'Konstrukcja zabawowa',
            text: [
              'Na konstrukcję można wchodzić wyłącznie w **skarpetach lub rajstopach**. Ze względu na zastosowane materiały antypoślizgowe korzystanie z konstrukcji w obuwiu jest zabronione – grozi to m.in. skręceniem kostki.'
            ]
          },
          {
            n: 3,
            title: 'Jedzenie i napoje',
            text: [
              'Ze względów higienicznych jedzenie i napoje można spożywać wyłącznie w sali urodzinowej. Nie wolno wnosić ich do sali z konstrukcją zabawową.'
            ]
          },
          {
            n: 4,
            title: 'Toalety',
            text: [
              'Do toalet należy wchodzić w specjalnych „kapciach-szczurkach” dostępnych na korytarzu przed toaletami. Można ich używać wyłącznie w toaletach.'
            ]
          },
          {
            n: 5,
            title: 'Zachowanie uczestników',
            text: ['Osoby, które:'],
            bullets: [
              'zakłócają funkcjonowanie Brykolandii,',
              'stwarzają zagrożenie dla siebie lub innych,',
              'nie stosują się do zaleceń personelu,'
            ],
            after: ['mogą zostać wyproszone z Brykolandii **bez prawa do zwrotu opłaty za wstęp**.']
          },
          {
            n: 6,
            title: 'Opieka i rzeczy osobiste',
            text: [
              'Personel Brykolandii nie sprawuje indywidualnej opieki nad dziećmi i nie odpowiada za rzeczy pozostawione lub zgubione na terenie Brykolandii.'
            ]
          },
          {
            n: 7,
            title: 'Dzieci do 5 lat',
            text: [
              'Dzieci do 5. roku życia mogą przebywać w Brykolandii wyłącznie pod opieką osoby dorosłej.'
            ]
          },
          {
            n: 8,
            title: 'Zdjęcia i nagrania',
            text: [
              'Brykolandia zastrzega sobie możliwość publikowania zdjęć i nagrań wykonanych podczas imprezy urodzinowej na swoich stronach i portalach internetowych oraz w materiałach reklamowych usługodawcy.'
            ]
          },
          {
            n: 9,
            title: 'Akceptacja zasad',
            text: [
              'Udział w imprezie oznacza akceptację **Regulaminu urodzin w Brykolandii** oraz **Polityki prywatności usługodawcy (RODO)**.'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'dane-osobowe-i-monitoring',
    number: '06',
    title: 'Dane osobowe, wizerunek i monitoring',
    summary:
      'Informacje o przetwarzaniu danych osobowych, fotografowaniu i monitoringu w Brykolandii.',
    clauses: [
      {
        n: 1,
        text: [
          'Dane osobowe klientów oraz uczestników usług świadczonych przez Brykolandię są przetwarzane zgodnie z obowiązującymi przepisami dotyczącymi ochrony danych osobowych. Szczegółowe informacje dotyczące administratora danych, celów i podstaw ich przetwarzania oraz praw osób, których dane dotyczą, określa Polityka prywatności.'
        ]
      },
      {
        n: 2,
        text: [
          'Podczas imprez i wydarzeń organizowanych w Brykolandii mogą być wykonywane fotografie oraz nagrania dokumentujące ich przebieg.'
        ]
      },
      {
        n: 3,
        text: [
          'Wybrane fotografie i nagrania mogą być wykorzystywane w materiałach informacyjnych i promocyjnych Brykolandii, w tym na stronie internetowej oraz w mediach społecznościowych, z poszanowaniem prawa do wizerunku i przepisów dotyczących ochrony danych osobowych.'
        ]
      },
      {
        n: 4,
        text: [
          'W przypadkach, w których rozpowszechnianie rozpoznawalnego wizerunku uczestnika wymaga zgody, publikacja następuje po uzyskaniu wymaganej zgody rodzica lub opiekuna prawnego.'
        ]
      },
      {
        n: 5,
        text: [
          'Rodzic lub opiekun może poinformować personel Brykolandii, że nie życzy sobie fotografowania dziecka lub publikowania jego wizerunku.'
        ]
      },
      {
        n: 6,
        text: [
          'Na terenie całego obiektu PUHW Perfekt, w tym Brykolandii, stosowany jest monitoring wizyjny rejestrujący obraz.'
        ]
      },
      {
        n: 7,
        text: [
          'Nagrania z monitoringu przechowywane są przez okres 1 miesiąca, po którym zapis jest automatycznie nadpisywany, chyba że zachodzi zgodna z prawem potrzeba zabezpieczenia określonego nagrania na dłuższy okres.'
        ]
      },
      {
        n: 8,
        text: [
          'Szczegółowe informacje dotyczące zasad stosowania monitoringu i przetwarzania związanych z nim danych osobowych znajdują się w Polityce prywatności.'
        ]
      }
    ]
  },
  {
    id: 'jakosc-uslug-i-reklamacje',
    number: '07',
    title: 'Jakość usług i reklamacje',
    summary: 'Zasady zgłaszania uwag i reklamacji oraz terminy rozpatrywania zgłoszeń.',
    clauses: [
      {
        n: 1,
        text: [
          'Wszelkie uwagi lub zastrzeżenia dotyczące prawidłowości lub jakości świadczonych usług klient może zgłaszać personelowi Brykolandii na piśmie podczas korzystania z usług lub niezwłocznie po jego zakończeniu.'
        ]
      },
      {
        n: 2,
        text: [
          'Zgłoszenie powinno zawierać opis przedmiotu uwag lub zastrzeżeń, żądanie klienta oraz datę zdarzenia stanowiącego podstawę zgłoszenia.'
        ]
      },
      {
        n: 3,
        text: [
          'W celu umożliwienia rzetelnego wyjaśnienia okoliczności zdarzenia zaleca się jego zgłoszenie możliwie szybko, w szczególności w okresie, w którym dostępny jest zapis z monitoringu.'
        ]
      },
      {
        n: 4,
        text: [
          'Właściciel Brykolandii rozpatruje zgłoszenie i informuje klienta o sposobie jego rozpatrzenia zgodnie z obowiązującymi przepisami.'
        ]
      }
    ]
  },
  {
    id: 'postanowienia-koncowe',
    number: '08',
    title: 'Postanowienia końcowe',
    summary: 'Postanowienia końcowe, właściwość sądu oraz zmiany w regulaminie.',
    clauses: [
      {
        n: 1,
        text: [
          'Wszelkie spory wynikające ze świadczenia usług strony będą w pierwszej kolejności starały się rozwiązać polubownie.'
        ]
      },
      {
        n: 2,
        text: [
          'W przypadku braku możliwości polubownego rozwiązania sporu będzie on rozstrzygany przez właściwy sąd zgodnie z obowiązującymi przepisami prawa.'
        ]
      },
      {
        n: 3,
        text: [
          'W sprawach nieuregulowanych niniejszym regulaminem zastosowanie mają odpowiednie przepisy Kodeksu cywilnego oraz inne powszechnie obowiązujące przepisy prawa.'
        ]
      },
      {
        n: 4,
        text: [
          'Skorzystanie z usług Brykolandii oznacza potwierdzenie zapoznania się z niniejszym regulaminem oraz zobowiązanie do przestrzegania jego postanowień.'
        ]
      },
      {
        n: 5,
        text: [
          'Właściciel Brykolandii zastrzega sobie prawo do wprowadzania zmian w cenniku oraz regulaminie. Aktualna wersja cennika i regulaminu dostępna jest na stronie internetowej oraz w Brykolandii.'
        ]
      }
    ]
  }
]
